import React, { useState } from "react";
import { Card, Badge, Button, InfoTooltip } from "../../../components";
import type { LoadSource, SourceType, SourceGroup } from "../domain/types";
import { SOURCE_TYPE_LABELS } from "../domain/types";

const SOURCE_BADGE_VARIANT: Record<SourceType, "quantum" | "health" | "ecosystem" | "economic" | "warning" | "neutral"> = {
  solar: "economic",
  wind: "quantum",
  ng: "health",
  bess: "ecosystem",
};

interface Props {
  source: LoadSource;
  onDelete?: (sourceId: string) => void;
  onEdit?: (source: LoadSource) => void;
}

export function LoadSourceCard({ source, onDelete, onEdit }: Props) {
  const type = source.source_type as SourceType;
  const label = SOURCE_TYPE_LABELS[type] || source.source_type;
  const variant = SOURCE_BADGE_VARIANT[type] || "neutral";

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-body font-medium text-text-primary">
              {source.source_name}
            </span>
            <Badge variant={variant}>
              {label}
            </Badge>
          </div>
          <div className="flex gap-1">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(source)}
              >
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(source.source_id)}
              >
                Remove
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-body-sm">
          <div>
            <span className="text-text-muted">
              Capacity <InfoTooltip text="Nameplate (AC) capacity of this generation or storage source." position="bottom" />
            </span>
            <div className="text-text-primary font-medium">
              {source.nameplate_capacity_mw} MW
            </div>
          </div>

          {type === "ng" && source.ng_turbine_type && (
            <div>
              <span className="text-text-muted">Turbine Type</span>
              <div className="text-text-primary font-medium uppercase">
                {source.ng_turbine_type}
              </div>
            </div>
          )}

          {type === "bess" && source.bess_capacity_mwh && (
            <>
              <div>
                <span className="text-text-muted">
                  Storage <InfoTooltip text="Total energy storage capacity in MWh." position="bottom" />
                </span>
                <div className="text-text-primary font-medium">
                  {source.bess_capacity_mwh} MWh
                </div>
              </div>
              <div>
                <span className="text-text-muted">
                  Duration <InfoTooltip text="Hours of discharge at full power rating." position="bottom" />
                </span>
                <div className="text-text-primary font-medium">
                  {source.bess_duration_hours || "4"}h
                </div>
              </div>
            </>
          )}

          {(type === "solar" || type === "wind") && (
            <div>
              <span className="text-text-muted">
                Profile <InfoTooltip text="SAM: auto-generated from NREL System Advisor Model. Custom: user-provided 8760 profile." position="bottom" />
              </span>
              <div className="text-text-primary font-medium">
                {source.sam_profile_flag ? "SAM" : "Custom"}
              </div>
            </div>
          )}

          {type === "ng" && source.ng_min_load_fraction != null && (
            <div>
              <span className="text-text-muted">
                Min Load <InfoTooltip text="Minimum output when running, as a fraction of capacity. Prevents frequent on/off cycling." position="bottom" />
              </span>
              <div className="text-text-primary font-medium">
                {(source.ng_min_load_fraction * 100).toFixed(0)}%
              </div>
            </div>
          )}

          {type === "ng" && source.ng_has_scr && (
            <div>
              <span className="text-text-muted">
                Emissions <InfoTooltip text="Selective Catalytic Reduction reduces NOx emissions, lowering health and ecosystem impacts from on-site generation." position="bottom" />
              </span>
              <div className="flex items-center gap-1.5">
                <Badge variant="quantum">SCR</Badge>
                <span className="text-text-primary font-medium text-body-sm">
                  {((source.ng_scr_nox_reduction_pct ?? 0.9) * 100).toFixed(0)}% NOx
                </span>
              </div>
            </div>
          )}

          {!source.is_active && (
            <div>
              <Badge variant="warning">Inactive</Badge>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ---- Grouped/stacked card for identical sources ---- */

interface GroupProps {
  group: SourceGroup;
  onDelete?: (sourceId: string) => void;
  onEdit?: (source: LoadSource) => void;
}

export function LoadSourceGroupCard({ group, onDelete, onEdit }: GroupProps) {
  const [expanded, setExpanded] = useState(false);
  const { baseName, sourceType, capacityEach, sources } = group;
  const count = sources.length;
  const totalMw = capacityEach * count;
  const variant = SOURCE_BADGE_VARIANT[sourceType] || "neutral";
  const label = SOURCE_TYPE_LABELS[sourceType] || sourceType;

  // Config detail line
  const sample = sources[0];
  let configLine = "";
  if (sourceType === "ng" && sample.ng_turbine_type)
    configLine = sample.ng_turbine_type.toUpperCase();
  else if ((sourceType === "solar" || sourceType === "wind") && sample.tech_type_id)
    configLine = sample.tech_type_id.replace("_", " ");
  else if (sourceType === "bess" && sample.bess_duration_hours)
    configLine = `${sample.bess_duration_hours}h / ${sample.bess_round_trip_eff ?? 0.85} eff`;

  return (
    <Card>
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-body font-medium text-text-primary">
              {baseName} &times; {count}
            </span>
            <Badge variant={variant}>{label}</Badge>
            <span className="text-body-sm text-text-muted">
              {totalMw.toLocaleString()} MW ({capacityEach} MW each)
            </span>
          </div>
          <button
            className="text-body-sm text-quantum-400 hover:underline"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Collapse" : `Show ${count} units`}
          </button>
        </div>

        {/* Config summary */}
        {configLine && (
          <div className="text-body-sm text-text-muted mb-1">
            {configLine}
            {sourceType === "ng" && sample.ng_min_load_fraction != null && (
              <> &middot; Min load {(sample.ng_min_load_fraction * 100).toFixed(0)}%</>
            )}
            {sourceType === "ng" && sample.ng_has_scr && (
              <> &middot; <span className="text-quantum-400">SCR {((sample.ng_scr_nox_reduction_pct ?? 0.9) * 100).toFixed(0)}%</span></>
            )}
          </div>
        )}

        {/* Expanded: individual source list */}
        {expanded && (
          <div className="mt-3 space-y-1 border-t border-border pt-3">
            {sources.map((src) => (
              <div
                key={src.source_id}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-surface-600"
              >
                <span className="text-body-sm text-text-primary">
                  {src.source_name}
                  {!src.is_active && (
                    <Badge variant="warning" className="ml-2">
                      Inactive
                    </Badge>
                  )}
                </span>
                <div className="flex gap-1">
                  {onEdit && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(src)}>
                      Edit
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(src.source_id)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
